import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';

function formatReadCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toString();
}

export default function HotGossipGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const hotArticles = articles.filter((a) => a.isHot).slice(0, 8);

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-5 rounded-full bg-primary-500"></div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                热点爆料
              </h2>
            </div>
            <p className="text-sm text-foreground-500">全网最热吃瓜内容，实时追踪</p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="向左滚动"
            >
              <i className="ri-arrow-left-s-line text-foreground-600"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="向右滚动"
            >
              <i className="ri-arrow-right-s-line text-foreground-600"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {hotArticles.map((article, idx) => {
            const category = categories.find((c) => c.slug === article.category);
            return (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="flex-shrink-0 w-[280px] md:w-[300px] snap-start group cursor-pointer"
              >
                <div className="relative rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading={idx < 4 ? 'eager' : 'lazy'}
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    {category && (
                      <span className="px-2.5 py-1 rounded-full bg-primary-500/90 backdrop-blur-sm text-white text-xs font-medium">
                        {category.name}
                      </span>
                    )}
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-md bg-accent-500 text-white text-xs font-bold">
                      HOT
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatReadCount(article.readCount)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {formatReadCount(article.commentCount)}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-300">{article.publishDate}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}